import { ensureAppSnapshot, clearAppSnapshot, type AppSnapshot } from "./appAccessSnapshot";
import { SessionEndedError } from './sessionErrors';
import { beginSuiteSignIn } from '../helpers/suiteAuthFlow';
import { TabUserStore, sharedSession } from './sharedSession';
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
export const centralEnabled = true;
export const accountsPortal =
  import.meta.env.VITE_ACCOUNTS_PORTAL || "https://accounts.smoothbadger.com";
export const accountsApi = (
  import.meta.env.VITE_ACCOUNTS_API || "https://accounts-api.smoothbadger.com"
).replace(/\/$/, "");
const authority = `${accountsApi}/`;
const clientId = import.meta.env.VITE_ACCOUNTS_CLIENT_ID || "cms";
const userKey = `oidc.user:${authority}:${clientId}`;
const tabUsers = new TabUserStore(userKey);
export const centralManager: UserManager = new UserManager({
  authority,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  response_type: "code",
  scope: "openid profile email accounts-api cms-api offline_access",
  loadUserInfo: false,
  automaticSilentRenew: false,
  userStore: tabUsers!,
  stateStore: new WebStorageStateStore({ store: sessionStorage }),
});
export const centralUser = (): User | null => {
  const value = sessionStorage.getItem(`oidc.user:${authority}:${clientId}`);
  try {
    return value ? User.fromStorageString(value) : null;
  } catch {
    return null;
  }
};
const shared = sharedSession(centralManager, tabUsers, userKey);
centralManager?.events.addUserUnloaded(() => { clearAppSnapshot(); window.dispatchEvent(new Event('cms-session-ended')); });
export async function centralAccessToken(): Promise<string> {
  try { return await shared!.accessToken(); }
  catch (error) { if (error instanceof SessionEndedError) window.dispatchEvent(new Event('cms-session-ended')); throw error; }
}
export async function centralSignOut() {
  return shared!.exclusive(async () => {
  const csrf = await fetch(`${accountsApi}/api/1/auth/csrf`, {
    credentials: "include",
  });
  if (!csrf.ok) throw new Error("Unable to contact Accounts to sign out.");
  const { token } = await csrf.json();
  const result = await fetch(`${accountsApi}/api/1/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-TOKEN": token },
  });
  if (!result.ok && result.status !== 401)
    throw new Error("Sign-out failed. Please retry.");
  localStorage.setItem("cms-explicit-signout", "true");
  await centralManager.removeUser();
  localStorage.removeItem("smoothbadger.session");
  sessionStorage.removeItem("smoothbadger.session");
  window.location.assign("/signin");
  });
}
// A rejected API token must not trigger another automatic SSO round trip.
export async function rejectCentralSession(token: string) {
  const current = await centralManager.getUser();
  if (!current || current.access_token !== token) return;
  sessionStorage.setItem('cms-session-rejected', 'true');
  sessionStorage.setItem('cms-auth-error', 'Your CMS session could not be verified. Sign in again to continue.');
  await centralManager.removeUser();
}
export async function accountsRequest<T>(
  path: string,
  method = "GET",
  body?: unknown,
): Promise<T> {
  const token = await centralAccessToken();
  const response = await fetch(`${accountsApi}/api/1${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 401) {
    await rejectCentralSession(token)
  }
  if (response.status === 204) return undefined as T
  const text = await response.text()
  let result: { detail?: string; title?: string } = {}
  try { result = text ? JSON.parse(text) : {} } catch { throw new Error(`Accounts returned an unexpected response (${response.status}).`) }
  if (!response.ok)
    throw new Error(
      result.detail ||
        result.title ||
        "Accounts could not complete the request.",
    );
  return result as T;
}
export type Workspace = {
  resourceId: string;
  businessId: string;
  accountId: string;
  localUserId: string;
  name: string;
  role: number;
  localMembershipId: string;
  canProvision: boolean;
  canManageBilling: boolean;
  entitlement: {
    hasAccess: boolean;
    reason: string;
    accessUntil?: string;
    trialEndsAt?: string;
    showTrialWarning: boolean; requiresTrialConversion?: boolean;
    showExpiryWarning: boolean;
  };
};
export type DirectoryContext = {
  userId: string;
  email: string;
  displayName: string;
  workspaces: Workspace[];
};


export type CmsSession = { context: DirectoryContext; isSystemAdmin: boolean };
export const cmsSession = (): CmsSession | null => {
  try { return JSON.parse(sessionStorage.getItem('cms-directory') || 'null'); } catch { return null; }
};
export async function refreshCmsSession(): Promise<CmsSession> {
  const token = await centralAccessToken();
  const { appEnv } = await import('../config/env');
  const response = await fetch(`${appEnv.apiUrl}/CentralAccounts/Context`, { headers: { Authorization: `Bearer ${token}` } });
  if (response.status === 401) await rejectCentralSession(token);
  if (!response.ok) throw new Error('Unable to verify your CMS access. Existing records are safe; please retry or contact your administrator.');
  const session = await response.json() as CmsSession;
  void ensureAppSnapshot(centralUser(), () => accountsRequest<AppSnapshot>("/identity/apps"));
  sessionStorage.setItem('cms-directory', JSON.stringify(session));
  const selected = localStorage.getItem(appEnv.storageCurrentSiteKey);
  if (!session.context.workspaces.some(x => x.businessId === selected)) {
    localStorage.removeItem(appEnv.storageCurrentSiteKey);
    if (session.context.workspaces.length === 1) localStorage.setItem(appEnv.storageCurrentSiteKey, session.context.workspaces[0].businessId);
  }
  return session;
}

export function centralSignIn(returnTo: unknown = '/content'): Promise<void> {
  return beginSuiteSignIn('cms', options => centralManager.signinRedirect(options), returnTo);
}
